/**
 * "Create orchestrator" modal launched from the workflow list kebab.
 *
 * Three states drive the body:
 *
 *   - confirm  -- one-line summary of what the action will do, plus
 *                 an explicit warning that the bearer is once-only.
 *                 Cancel / Create.
 *   - loading  -- progress copy driven by the service's onProgress
 *                 callback (publishing -> describing -> creating).
 *   - success  -- the once-only bearer in a copyable code block,
 *                 the A2A endpoint URL, the AgentCard URL, and a
 *                 button to jump into the new agent's config page.
 *
 * The bearer is only ever shown to the operator here.  Subsequent
 * GETs on the agent expose only the 8-char prefix (server-side
 * platform-api never returns the raw token again).  We make this
 * explicit in the success-state copy so the operator copies it.
 */

import { FC, useContext, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Bot, Check, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { AuthContext } from '@/context/auth-context';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useNotification } from '@/components/notifications';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import {
  createOrchestratorForWorkflow,
  OrchestratorProgress,
} from '@/services/agents/create-orchestrator-for-workflow';
import { ROUTES } from '@/constants/routes';
import type { AgentCreateResponse } from '@/types/agent-types';
import type { WorkflowDetails } from '@/types/workflow-types';

interface CreateOrchestratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  workflow: WorkflowDetails | null;
  /**
   * Optional callback fired when an orchestrator finishes creating.
   * The list page uses this to refresh its agents list (or just the
   * workflow's publish_to_api badge), since both got mutated.
   */
  onCreated?: (response: AgentCreateResponse) => void;
}

const _PROGRESS_COPY: Record<OrchestratorProgress, string> = {
  publishing_workflow: 'Publishing workflow to the A2A API...',
  generating_description: 'Generating agent description...',
  creating_agent: 'Creating orchestrator agent and minting bearer...',
  done: 'Done.',
};

export const CreateOrchestratorModal: FC<CreateOrchestratorModalProps> = ({
  isOpen,
  onClose,
  orgId,
  workflow,
  onCreated,
}) => {
  const navigate = useOrgNavigate();
  const notification = useNotification();
  const { user, isAdmin } = useContext(AuthContext);

  // The agent config route is owner/admin-only. Members can still mint an
  // orchestrator (and copy its bearer here), but the deep-link into config
  // would just bounce them off the route guard, so only offer it to managers.
  const canManageAgents = isAdmin || !!user?.isOwner;

  const [phase, setPhase] = useState<'confirm' | 'loading' | 'success'>(
    'confirm',
  );
  const [progress, setProgress] = useState<OrchestratorProgress | null>(null);
  const [result, setResult] = useState<AgentCreateResponse | null>(null);
  const [bearerCopied, setBearerCopied] = useState(false);

  const handleClose = () => {
    // Reset state so the next open starts on the confirm step rather
    // than re-showing the prior success bearer.
    setPhase('confirm');
    setProgress(null);
    setResult(null);
    setBearerCopied(false);
    onClose();
  };

  const handleCreate = async () => {
    if (!workflow || !orgId) return;
    setPhase('loading');
    try {
      const response = await createOrchestratorForWorkflow(orgId, workflow, {
        onProgress: setProgress,
      });
      setResult(response);
      setPhase('success');
      notification.success(`Created orchestrator agent for "${workflow.name}"`);
      onCreated?.(response);
    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          context: 'create-orchestrator-modal',
          orgId,
          workflowId: workflow?.workflow_id,
        },
      });
      const message =
        err instanceof Error ? err.message : 'Failed to create orchestrator';
      notification.error(
        `Could not create orchestrator for "${workflow.name}": ${message}`,
      );
      // Fall back to the confirm step so the operator can retry / cancel
      // without losing the modal context.
      setPhase('confirm');
      setProgress(null);
    }
  };

  const handleCopyBearer = async () => {
    if (!result?.a2a_bearer_token) return;
    try {
      await navigator.clipboard.writeText(result.a2a_bearer_token);
      setBearerCopied(true);
      notification.success('Bearer token copied to clipboard');
    } catch {
      notification.error(
        'Could not copy to clipboard. Select the bearer text manually.',
      );
    }
  };

  const handleOpenAgentConfig = () => {
    if (!result?.agent?.id) return;
    navigate(ROUTES.AGENTS.CONFIG.replace(':agentId', result.agent.id));
    handleClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Bot className='size-5' />
            Create orchestrator agent
          </DialogTitle>
          {workflow && (
            <DialogDescription>
              An A2A orchestrator agent will be created for the workflow{' '}
              <span className='font-medium text-foreground'>
                {workflow.name}
              </span>
              .
            </DialogDescription>
          )}
        </DialogHeader>

        {phase === 'confirm' && workflow && (
          <div className='space-y-3 text-sm'>
            <p className='text-muted-foreground'>This will:</p>
            <ul className='ml-4 list-disc space-y-1 text-muted-foreground'>
              <li>
                Mark{' '}
                <span className='font-medium text-foreground'>
                  {workflow.name}
                </span>{' '}
                as <code className='text-xs'>publish_to_api: true</code>
                {workflow.publish_to_api ? ' (already enabled, skipped)' : ''}.
              </li>
              <li>
                Create a new A2A orchestrator agent with this workflow in its
                allowlist.
              </li>
              <li>Mint a one-time bearer token for the agent.</li>
            </ul>
            <p className='rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-200'>
              The bearer token is shown only once after creation. Copy it
              immediately if you need to use it from outside the platform.
            </p>
          </div>
        )}

        {phase === 'loading' && (
          <div className='flex flex-col items-center gap-3 py-6 text-sm'>
            <Loader2 className='size-6 animate-spin text-primary' />
            <p className='text-muted-foreground'>
              {progress
                ? _PROGRESS_COPY[progress]
                : 'Creating orchestrator agent...'}
            </p>
          </div>
        )}

        {phase === 'success' && result && (
          <div className='space-y-4 text-sm'>
            <p className='text-muted-foreground'>
              Created agent{' '}
              <span className='font-medium text-foreground'>
                {result.agent.name}
              </span>
              .
            </p>

            <div>
              <div className='mb-1 flex items-center justify-between'>
                <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                  A2A bearer token (one-time)
                </span>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={handleCopyBearer}
                  className='h-7 gap-1 text-xs'
                >
                  {bearerCopied ? (
                    <>
                      <Check className='size-3' /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className='size-3' /> Copy
                    </>
                  )}
                </Button>
              </div>
              <code className='block break-all rounded-md border bg-muted p-2 text-xs'>
                {result.a2a_bearer_token}
              </code>
            </div>

            <div className='space-y-1'>
              <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                A2A endpoint
              </span>
              <code className='block break-all rounded-md border bg-muted p-2 text-xs'>
                {result.a2a_endpoint_url}
              </code>
            </div>

            <div className='space-y-1'>
              <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                Agent card
              </span>
              <code className='block break-all rounded-md border bg-muted p-2 text-xs'>
                {result.a2a_agent_card_url}
              </code>
            </div>
          </div>
        )}

        <DialogFooter>
          {phase === 'confirm' && (
            <>
              <Button variant='outline' onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={!workflow}>
                Create orchestrator
              </Button>
            </>
          )}
          {phase === 'loading' && (
            <Button variant='outline' disabled>
              Working...
            </Button>
          )}
          {phase === 'success' && (
            <>
              <Button variant='outline' onClick={handleClose}>
                Done
              </Button>
              {canManageAgents && (
                <Button onClick={handleOpenAgentConfig} className='gap-1'>
                  Open agent config <ExternalLink className='size-3' />
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
